import React from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../SEO'; // Import the SEO component
import { productsByCategory } from './productsData';

function ProductDetail() {
    const { id } = useParams();
    const product = productsByCategory.All.find(p => String(p.id) === id);

    if (!product) {
        return (
            <div className="container my-4">
                <h2 style={{ fontFamily: 'Georgia', color: '#355E3B', fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>
                    Product Not Found
                </h2>
                <p style={{ fontFamily: 'Georgia', color: '#666', fontSize: 'clamp(1rem, 2.5vw, 1.2rem)' }}>
                    The product you are looking for is not available. Please check our <Link to="/products" style={{ color: '#C19A6B' }}>products page</Link> for more options.
                </p>
            </div>
        );
    }

    // SEO Structured Data
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": product.description.join(' '),
        "url": `https://www.galleceylonspices.com/product/${id}`,
        "image": product.detailImages[0] || product.image,
        "brand": {
            "@type": "Brand",
            "name": "Galle Ceylon Spices International"
        },
        "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "USD",
            "price": "0.00", // Placeholder, update if pricing is available
            "url": `https://www.galleceylonspices.com/product/${id}`
        },
        "publisher": {
            "@type": "Organization",
            "name": "Galle Ceylon Spices International (Pvt) Ltd",
            "logo": {
                "@type": "ImageObject",
                "url": "%PUBLIC_URL%/logo.png"
            },
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "448/2, Nawadagala",
                "addressLocality": "Elpitiya",
                "postalCode": "80000",
                "addressCountry": "Sri Lanka"
            },
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+94-77-929-1829",
                "contactType": "Customer Service",
                "contactOption": "WhatsApp",
                "areaServed": "Worldwide",
                "availableLanguage": "English"
            }
        }
    };

    return (
        <div className="container my-4 py-4">
            <SEO
                title={`${product.name} - Galle Ceylon Spices International`}
                description={`Discover ${product.name} from Galle Ceylon Spices International. ${product.description[0] || 'Premium quality Ceylon spices.'}`}
                keywords={`${product.name.toLowerCase()}, Ceylon spices, Galle Ceylon Spices, Sri Lankan spices, cinnamon, whole spices, spice powders`}
                canonicalUrl={`https://www.galleceylonspices.com/product/${id}`}
                ogImage={product.detailImages[0] || product.image || '%PUBLIC_URL%/logo.png'}
                structuredData={structuredData}
            />
            <section
                className="py-4 rounded-3 shadow-lg animate__animated animate__fadeIn"
                style={{
                    animationDuration: '1s',
                    backgroundColor: 'rgba(193, 154, 107, 0.1)',
                }}
            >
                <div className="mb-3">
                    <Link
                        to="/products"
                        className="btn back-button"
                        aria-label="Back to products page"
                        style={{
                            backgroundColor: '#C19A6B',
                            borderColor: '#C19A6B',
                            color: '#FFFFFF',
                            fontFamily: 'Georgia',
                            fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                            padding: 'clamp(6px, 1.5vw, 10px) clamp(12px, 3vw, 20px)',
                        }}
                    >
                        Back to Products
                    </Link>
                </div>
                <h2
                    className="text-center mb-4 position-relative"
                    style={{
                        fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
                        letterSpacing: '0.5px',
                        color: '#355E3B',
                        fontFamily: 'Georgia',
                        lineHeight: '1.2',
                    }}
                >
                    {product.name}
                    <span
                        style={{
                            position: 'absolute',
                            bottom: '-8px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '80px',
                            height: '2px',
                            backgroundColor: '#C19A6B',
                            borderRadius: '2px',
                        }}
                    />
                </h2>
                <div className="row g-3 mb-4 justify-content-center">
                    {product.detailImages.map((image, index) => (
                        <div
                            key={index}
                            className="col-12 col-md-6 col-lg-4 col-10 animate__animated animate__fadeInUp"
                            style={{ animationDelay: '0.2s', animationDuration: '0.8s' }}
                        >
                            <div
                                className="card shadow-sm border-0 product-image-card"
                                style={{
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    border: '1px solid rgba(193, 154, 107, 0.3)',
                                }}
                            >
                                <div style={{ padding: '10px' }}>
                                    <img
                                        src={image}
                                        alt={`${product.name} detail ${index + 1}`}
                                        className="img-fluid rounded-3"
                                        style={{
                                            objectFit: 'contain',
                                            width: '100%',
                                            height: 'clamp(150px, 30vw, 250px)',
                                        }}
                                        onError={(e) => (e.target.src = '/path/to/placeholder.jpg')}
                                        loading="lazy"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="px-3 animate__animated animate__fadeInUp" style={{ animationDelay: '0.4s', animationDuration: '0.8s' }}>
                    {product.description.map((paragraph, index) => (
                        <p
                            key={index}
                            className="text-muted mb-3"
                            style={{
                                lineHeight: '1.8',
                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                fontFamily: 'Georgia',
                                color: '#355E3B',
                            }}
                        >
                            {paragraph}
                        </p>
                    ))}
                </div>
            </section>

            {/* Inline CSS for styles and responsiveness */}
            <style>
                {`
                    .back-button {
                        transition: all 0.3s ease-in-out;
                        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
                    }
                    .back-button:hover, .back-button:focus {
                        background-color: #A67B4D !important;
                        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
                        transform: scale(1.05);
                    }
                    .product-image-card {
                        transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
                    }
                    .product-image-card img {
                        transition: transform 0.3s ease-in-out;
                    }
                    .product-image-card:hover img {
                        transform: scale(1.05);
                    }
                    .product-image-card:hover {
                        box-shadow: 0 6px 15px rgba(193, 154, 107, 0.5);
                    }
                    @media (max-width: 576px) {
                        .container.my-4 {
                            margin-top: 1rem !important;
                            margin-bottom: 1rem !important;
                        }
                        .container.py-4 {
                            padding-top: 1rem !important;
                            padding-bottom: 1rem !important;
                        }
                        .py-4 {
                            padding-top: 1rem !important;
                            padding-bottom: 1rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 1rem !important;
                        }
                        .px-3 {
                            padding-left: 0.75rem !important;
                            padding-right: 0.75rem !important;
                        }
                        img.img-fluid {
                            height: 150px !important;
                        }
                        .btn {
                            padding: 6px 12px !important;
                            font-size: 0.9rem !important;
                        }
                    }
                    @media (max-width: 576px) and (orientation: landscape) {
                        img.img-fluid {
                            height: 120px !important;
                        }
                        .px-3 {
                            padding-left: 0.5rem !important;
                            padding-right: 0.5rem !important;
                        }
                        .btn {
                            padding: 4px 10px !important;
                            font-size: 0.8rem !important;
                        }
                    }
                    @media (min-width: 577px) and (max-width: 768px) {
                        .container.my-4 {
                            margin-top: 2rem !important;
                            margin-bottom: 2rem !important;
                        }
                        .container.py-4 {
                            padding-top: 2rem !important;
                            padding-bottom: 2rem !important;
                        }
                        .py-4 {
                            padding-top: 1.5rem !important;
                            padding-bottom: 1.5rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 1.5rem !important;
                        }
                        img.img-fluid {
                            height: 200px !important;
                        }
                    }
                    @media (min-width: 769px) and (max-width: 1024px) {
                        .container.my-4 {
                            margin-top: 3rem !important;
                            margin-bottom: 3rem !important;
                        }
                        .container.py-4 {
                            padding-top: 3rem !important;
                            padding-bottom: 3rem !important;
                        }
                        .py-4 {
                            padding-top: 2rem !important;
                            padding-bottom: 2rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 2rem !important;
                        }
                        img.img-fluid {
                            height: 220px !important;
                        }
                    }
                    @media (min-width: 1025px) {
                        .container.my-4 {
                            margin-top: 4rem !important;
                            margin-bottom: 4rem !important;
                        }
                        .container.py-4 {
                            padding-top: 4rem !important;
                            padding-bottom: 4rem !important;
                        }
                        .py-4 {
                            padding-top: 2.5rem !important;
                            padding-bottom: 2.5rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 2.5rem !important;
                        }
                        img.img-fluid {
                            height: 250px !important;
                        }
                    }
                `}
            </style>
        </div>
    );
}

export default ProductDetail;